import React, { useState, useEffect, useMemo } from 'react';
import { Container, Typography, Box, Paper, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, TextField, Select, MenuItem, FormControl, InputLabel, Backdrop, Grid, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Check as CheckIcon, Security as SecurityIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { ThemeProvider } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';
import { theme as customTheme } from '../../../../website/styles/theme';
import { PageSkeleton } from '../../../loading/skeletons';
import { auth } from '../../../../auth/appAuth';
import { fetchCurrentUser } from '../../../../api/me';
import { getApiToken } from '../../../../api/authToken';
import { patchCompany } from '../../../../api/company/company';
import {
  getPackage,
  listBillingHistory,
  listPackages,
  patchCompanySubscription,
  upsertGatewaySubscription,
} from '../../../../api/subscriptions/subscriptions';
import { formatBillingAmount, formatBillingDate } from '../../../../packages/utils/billingDb';
import { cancelSubscription, renewSubscription, upgradeSubscription, changeBillingCycle, convertTrialToPaid } from '../../../../website/services/payment/subscriptionService';
import { getSubscriptionAuthorizeEnvironment } from '../../../../website/services/payment/authorizeEnvironment';
import { useNotification } from '../../../../context/NotificationContext';
import { useAuth } from '../../../../context/AuthContext';

import DeluxeVaultSetup from '../../../../deluxe/components/DeluxeVaultSetup';
import '../AccountSettings/AccountSettings.css';
import './ManageSubscription.css';
import Autocomplete from '@mui/material/Autocomplete';
import HomeIcon from '@mui/icons-material/Home';
import LocationCityIcon from '@mui/icons-material/LocationCity';

// Create MUI theme for Billing page
const muiTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: customTheme.colors.primary.main,
      light: customTheme.colors.primary.light,
      dark: customTheme.colors.primary.dark,
      contrastText: '#fff',
    },
    success: {
      main: '#4caf50',
      light: '#81c784',
      dark: '#388e3c',
      contrastText: '#fff',
    },
    error: {
      main: '#f44336',
      light: '#e57373',
      dark: '#d32f2f',
      contrastText: '#fff',
    },
    warning: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
      contrastText: '#fff',
    },
    text: {
      primary: customTheme.colors.text.primary,
      secondary: customTheme.colors.text.secondary,
      disabled: customTheme.colors.text.light,
    },
    background: {
      default: customTheme.colors.background.primary,
      paper: customTheme.colors.background.secondary,
    },
  },
  typography: {
    fontFamily: customTheme.typography.fontFamily.sans,
  },
  spacing: 8,
  shape: {
    borderRadius: 4,
  },
});

// US States constant
const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' }
];

const StyledContainer = styled(Box)(({ theme }) => ({
  maxWidth: 800,
  margin: '0 auto',
  padding: theme.spacing(3),
}));

// Enhanced Paper styling for Billing page
const StyledPaper = styled(Paper)(({ theme }) => ({
  background: '#FFFFFF',
  borderRadius: '16px',
  padding: theme.spacing(4),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  border: '1px solid rgba(0, 102, 255, 0.1)',
  position: 'relative',
  overflow: 'hidden',
  transition: 'none !important',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08) !important',
    transform: 'none !important',
    cursor: 'default',
    transition: 'none !important',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #0066FF, #4D94FF)',
  },
  // Override any MUI default hover states
  '&.MuiPaper-root:hover': {
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08) !important',
  }
}));

const PrimaryButton = styled.button`
  border: none;
  display: flex;
  padding: 0.75rem 1.5rem;
  background-color: #488aec;
  color: #ffffff;
  font-size: 0.75rem;
  line-height: 1rem;
  font-weight: 700;
  font-family: 'Poppins', system-ui, -apple-system, BlinkMacSystemFont,
    'Segoe UI', sans-serif;
  letter-spacing: 0.05em;
  text-align: center;
  cursor: pointer;
  vertical-align: middle;
  align-items: center;
  border-radius: 0.5rem;
  user-select: none;
  gap: 0.75rem;
  box-shadow:
    0 4px 6px -1px #488aec31,
    0 2px 4px -1px #488aec17;
  transition: all 0.6s ease;

  &:hover {
    box-shadow:
      0 10px 15px -3px #488aec4f,
      0 4px 6px -2px #488aec17;
  }

  &:focus,
  &:active {
    opacity: 0.85;
    box-shadow: none;
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
  '@media (max-width: 600px)': {
    flexDirection: 'column',
  }
}));

const LoadingBackdrop = styled(Backdrop)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  color: '#fff',
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

// Loading overlay styles matching ReviewAndBillingStep
const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 400px;
  padding: 32px;
`;

const LoadingTitle = styled.h2`
  color: ${customTheme.colors.primary.main};
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 16px;
  font-family: ${customTheme.typography.fontFamily.sans};
`;

const LoadingSubtitle = styled.p`
  color: ${customTheme.colors.text.secondary};
  font-size: 16px;
  margin-bottom: 32px;
  font-family: ${customTheme.typography.fontFamily.sans};
`;

const ProgressContainer = styled.div`
  width: 100%;
  margin-bottom: 32px;
`;

const ProgressStep = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  opacity: ${props => props.completed ? 1 : 0.6};
  transition: opacity 0.3s ease;
`;

const StepIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  background: ${props => props.completed ? customTheme.colors.primary.main : customTheme.colors.secondary.dark};
  color: white;
  font-size: 12px;
  font-weight: 600;
`;

const StepText = styled.span`
  color: ${props => props.completed ? customTheme.colors.text.primary : customTheme.colors.text.secondary};
  font-size: 14px;
  font-weight: ${props => props.completed ? 500 : 400};
`;

const SpinnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 24px;
`;

const CustomSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${customTheme.colors.secondary.dark};
  border-top: 3px solid ${customTheme.colors.primary.main};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const SecurityBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 24px;
  padding: 8px 16px;
  background: ${customTheme.colors.secondary.light};
  border: 1px solid ${customTheme.colors.secondary.dark};
  border-radius: 8px;
  color: ${customTheme.colors.text.secondary};
  font-size: 14px;
`;

const Title = styled('h1')(({ theme }) => ({
  fontSize: customTheme.typography.fontSize['3xl'],
  color: theme.palette.text.primary,
  fontFamily: theme.typography.fontFamily,
  '@media (max-width: 768px)': {
    fontSize: customTheme.typography.fontSize['2xl'],
  }
}));

const ManageSubscription = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { showToast } = useNotification();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [company, setCompany] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [currentPackage, setCurrentPackage] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [renewDialogOpen, setRenewDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const [renewingStep, setRenewingStep] = useState(0);
  const [cancellationReason, setCancellationReason] = useState('');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [cycleDialogOpen, setCycleDialogOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [upgradingStep, setUpgradingStep] = useState(0);
  const [changingBillingCycle, setChangingBillingCycle] = useState(false);
  const [changingBillingCycleStep, setChangingBillingCycleStep] = useState(0);
  const [availablePackages, setAvailablePackages] = useState([]);
  const [convertingTrial, setConvertingTrial] = useState(false);
  const [trialConversionDialogOpen, setTrialConversionDialogOpen] = useState(false);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState('monthly');
  const [trialBillingAddress, setTrialBillingAddress] = useState({
    firstName: '',
    lastName: '',
    street: '',
    city: '',
    state: '',
    zipCode: ''
  });
  const [billingHistory, setBillingHistory] = useState([]);

  const cssVars = useMemo(() => ({
    '--as-bg': '#f6f8fc',
    '--as-surface': '#ffffff',
    '--as-surface-2': 'rgba(255, 255, 255, 0.7)',
    '--as-text': '#0f172a',
    '--as-muted': '#64748b',
    '--as-border': 'rgba(15, 23, 42, 0.12)',
    '--as-shadow': '0 10px 30px rgba(2, 6, 23, 0.08)',
    '--as-focus': 'rgba(72, 138, 236, 0.45)',
    '--as-primary': '#488aec',
    '--as-input-bg': 'rgba(255, 255, 255, 0.9)',
    '--as-input-bg-focus': '#ffffff',
  }), []);

  // Helper function to safely format dates
  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      if (typeof date?.toDate === 'function') {
        return date.toDate().toLocaleDateString();
      }
      if (typeof date === 'string') {
        return new Date(date).toLocaleDateString();
      }
      if (date instanceof Date) {
        return date.toLocaleDateString();
      }
      if (typeof date === 'number') {
        return new Date(date > 1e12 ? date : date * 1000).toLocaleDateString();
      }
      if (typeof date?.seconds === 'number') {
        return new Date(date.seconds * 1000).toLocaleDateString();
      }
      return 'N/A';
    } catch (err) {
      console.error('Error formatting date:', err);
      return 'N/A';
    }
  };

  const fetchCompanyDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getApiToken();
      const me = await fetchCurrentUser(token);
      const companyData = me.company;
      if (!companyData?.id) {
        setError('No company found for this user');
        return;
      }

      setCompany(companyData);
      if (companyData.package?.subscription) {
        setSubscription(companyData.package.subscription);
      }
      if (companyData.package?.id) {
        await fetchCurrentPackage(companyData.package.id);
      }
      await fetchBillingHistory(companyData.id);
    } catch (err) {
      console.error('Error fetching company details:', err);
      setError('Failed to load company details');
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentPackage = async (packageId) => {
    try {
      const packageData = await getPackage(packageId);
      if (packageData) {
        setCurrentPackage(packageData);
      }
    } catch (err) {
      console.error('Error fetching current package:', err);
    }
  };

  const fetchBillingHistory = async (companyId) => {
    try {
      const history = await listBillingHistory({ companyId, limit: 10 });
      setBillingHistory(history);
    } catch (err) {
      console.error('Error fetching billing history:', err);
    }
  };

  useEffect(() => {
    if (!user?.uid) return;
    fetchCompanyDetails();
  }, [user?.uid]);

  const getSubscriptionStatusBadge = () => {
    if (!subscription) return null;

    const statusConfig = {
      active: { className: 'verified', label: 'Active' },
      cancelled: { className: 'unverified', label: 'Cancelled' },
      suspended: { className: 'comingSoon', label: 'Suspended' },
      expired: { className: 'unverified', label: 'Expired' },
      pending: { className: 'comingSoon', label: 'Pending' },
      trial: { className: 'comingSoon', label: 'Trial' }
    };

    const config = statusConfig[subscription.status] || { className: 'comingSoon', label: subscription.status };

    return (
      <span className={`asVerifyBadge ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const handleCancelSubscription = async () => {
    try {
      setCancelling(true);

      if (!subscription?.id) {
        throw new Error('No subscription ID found. Please contact support.');
      }

      const response = await cancelSubscription(
        subscription.id,
        cancellationReason || 'User requested cancellation',
        getSubscriptionAuthorizeEnvironment(subscription)
      );

      if (response.success) {
        await patchCompanySubscription({
          companyId: company.id,
          subscription: {
            status: 'cancelled',
            cancelledAt: new Date().toISOString(),
            cancellationReason: cancellationReason || 'User requested cancellation',
            autoRenew: false,
          },
        });
        if (subscription.id) {
          await upsertGatewaySubscription({
            companyId: company.id,
            subscriptionId: subscription.id,
            subscription: { status: 'cancelled', autoRenew: false },
          });
        }
        await fetchCompanyDetails();
        setCancelDialogOpen(false);
        setCancellationReason('');
        showToast(
          'Subscription cancelled successfully. You will continue to have access until the end of your current billing period.',
          'success'
        );
      } else {
        throw new Error(response.message || 'Failed to cancel subscription');
      }
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      setError(err.message || 'Failed to cancel subscription. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  const handleRenewSubscription = async () => {
    try {
      console.log('Starting renewal process...');
      setRenewing(true);
      setRenewingStep(1);
      setError(null);
      
      // Close the dialog immediately when renewal starts
      setRenewDialogOpen(false);
      
      // Scroll to top when loading starts
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Add a delay to show step 1
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      if (!subscription?.id || !paymentDetails) {
        throw new Error('Missing subscription ID or payment details');
      }

      // Get the user's name
      const token = await getApiToken();
      const me = await fetchCurrentUser(token);
      const userData = me.user || {};
      const userName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();

      // Calculate the start date for the renewal and convert to ISO string
      const startDate = new Date().toISOString();
      
      const renewalData = {
        name: userName || auth.currentUser.email,
        amount: subscription.currentPrice,
        billingCycle: subscription.billingCycle,
        vault: paymentDetails,
        startDate: startDate,
        metadata: {
          businessId: company.id,
          packageId: company.package.id,
          packageName: company.package.name,
          email: auth.currentUser.email,
          companyName: company.companyName,
          phone: company.phone,
          isRenewal: true,
          renewalStartDate: startDate
        },
        previousSubscriptionId: subscription.id
      };

      setRenewingStep(2);
      // Add a delay before the API call to show step 2
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const response = await renewSubscription(renewalData);

      if (response.success) {
        setRenewingStep(3);
        // Add a delay to show step 3
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Refresh the company details to get updated subscription status
        await fetchCompanyDetails();
        setRenewDialogOpen(false);
        setPaymentDetails(null);
        showToast('Subscription renewed successfully! Your subscription is now active.', 'success');
        if (!embedded) {
          navigate('/app/overview', { replace: true });
        }
      } else {
        throw new Error(response.message || 'Failed to renew subscription');
      }
    } catch (err) {
      console.error('Error renewing subscription:', err);
      setError(err.message || 'Failed to renew subscription. Please try again.');
    } finally {
      setRenewing(false);
      setRenewingStep(0);
      
      // Scroll to top when renewal is complete
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    let cancelled = false;

    const loadPackages = async () => {
      try {
        const allPackages = await listPackages();
        if (cancelled) return;
        const currentMonthlyPrice = parseFloat(
          company?.package?.subscription?.currentPrice || company?.package?.price?.monthly || 0
        );
        const currentPrice =
          company?.package?.subscription?.billingCycle === 'annually'
            ? currentMonthlyPrice / 12 / 0.8
            : currentMonthlyPrice;
        const packages = allPackages
          .filter(
            (pkg) =>
              !pkg.isAdminPackage &&
              pkg.id !== company?.package?.id &&
              !pkg.name?.toLowerCase().includes('trial') &&
              (company?.package?.name === 'Enterprise' ||
                company?.package?.name === 'Business Plus' ||
                parseFloat(pkg.price?.monthly || 0) > currentPrice)
          )
          .sort((a, b) => parseFloat(a.price?.monthly || 0) - parseFloat(b.price?.monthly || 0));
        setAvailablePackages(packages);
        if (company?.package?.id) {
          const currentPkg = allPackages.find((pkg) => pkg.id === company.package.id);
          if (currentPkg) setCurrentPackage(currentPkg);
        }
      } catch (err) {
        console.error('Error loading packages:', err);
      }
    };

    if (company?.package?.id) {
      loadPackages();
    }

    return () => {
      cancelled = true;
    };
  }, [company?.package?.id]);

  const handleUpgrade = async () => {
    try {
      console.log('Starting upgrade process...');
      console.log('handleUpgrade - Current state:', {
        subscriptionId: subscription?.id,
        subscriptionStatus: subscription?.status,
        companyPackageSubscriptionId: company?.package?.subscription?.id,
        companyPackageStatus: company?.package?.subscription?.status
      });
      
      // Debug subscription state
      debugSubscriptionState();
      setUpgrading(true);
      setUpgradingStep(1);
      setError(null);

      // Close the dialog immediately when upgrade starts
      setUpgradeDialogOpen(false);
      
      // Scroll to top when loading starts
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Add a delay to show step 1
      await new Promise(resolve => setTimeout(resolve, 2000));

      if (!selectedPackage || !paymentDetails) {
        throw new Error('Please select a package and provide payment details');
      }

      // Get user data from auth
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }

      if (!currentUser.email) {
        throw new Error('User email is required');
      }

      console.log('handleUpgrade - Current user:', {
        uid: currentUser.uid,
        email: currentUser.email
      });

      // Get user's name from company data or fallback to email
      const userName = `${company?.ownerFirstName || ''} ${company?.ownerLastName || ''}`.trim() || 
                      currentUser.email;

      // Calculate new amount based on selected package and current billing cycle
      const currentBillingCycle = company.package.subscription.billingCycle;
      let baseAmount = parseFloat(selectedPackage.price.monthly);
      
      if (currentBillingCycle === 'annually') {
        baseAmount = (baseAmount * 12) * 0.8; // 20% discount for annual
      }

      // Calculate tax (8.25%)
      const TAX_RATE = 0.0825;
      const taxAmount = baseAmount * TAX_RATE;
      const totalAmount = baseAmount + taxAmount;

      const upgradeData = {
        name: selectedPackage.name, // Use package name instead of customer name
        email: currentUser.email, // Include at root level
        amount: totalAmount.toFixed(2),
        baseAmount: baseAmount.toFixed(2),
        taxAmount: taxAmount.toFixed(2),
        taxRate: TAX_RATE,
        billingCycle: currentBillingCycle,
        vault: paymentDetails,
        metadata: {
          email: currentUser.email, // Include in metadata
          businessId: company.id,
          packageId: selectedPackage.id,
          packageName: selectedPackage.name,
          companyName: company.name || '',
          phone: company.phone || '',
          customerName: `${company?.ownerFirstName || ''} ${company?.ownerLastName || ''}`.trim() || currentUser.email,
          previousPackageId: company.package.id,
          previousSubscriptionId: subscription.id || company.package.subscription?.id,
          isUpgrade: true,
          billingAddress: {
            firstName: company?.ownerFirstName || '',
            lastName: company?.ownerLastName || '',
            street: company.address?.street || '',
            city: company.address?.city || '',
            state: company.address?.state || '',
            zipCode: company.address?.zipCode || '',
            country: 'USA'
          }
        }
      };

      setUpgradingStep(2);
      // Add a delay before the API call to show step 2
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      console.log('handleUpgrade - Calling upgradeSubscription with:', {
        ...upgradeData,
        paymentDetails: '***REDACTED***'
      });
      
      console.log('handleUpgrade - Subscription details:', {
        subscriptionId: subscription?.id,
        subscriptionStatus: subscription?.status,
        subscriptionExists: !!subscription,
        companyPackageSubscriptionId: company?.package?.subscription?.id,
        finalPreviousSubscriptionId: subscription.id || company.package.subscription?.id,
        upgradeDataPreviousSubscriptionId: upgradeData.metadata.previousSubscriptionId
      });

      const response = await upgradeSubscription(upgradeData);

      console.log('handleUpgrade - Response:', response);

      if (response.success) {
        const nextSubscription = {
          ...company.package.subscription,
          id: response.subscription?.id,
          status: 'active',
          currentPrice: totalAmount.toFixed(2),
          baseAmount: baseAmount.toFixed(2),
          taxAmount: taxAmount.toFixed(2),
          taxRate: TAX_RATE,
          packageId: selectedPackage.id,
          updatedAt: new Date().toISOString(),
          previousPackageId: company.package.id,
        };
        await patchCompany({
          companyId: company.id,
          company: {
            package: {
              ...company.package,
              id: selectedPackage.id,
              name: selectedPackage.name,
              features: selectedPackage.features || {},
              subscription: nextSubscription,
            },
          },
        });
        if (response.subscription?.id) {
          await upsertGatewaySubscription({
            companyId: company.id,
            subscriptionId: response.subscription.id,
            subscription: nextSubscription,
          });
        }

        setUpgradingStep(3);
        // Add a longer delay to show step 3
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Update local subscription state with the new subscription ID
        if (response.subscription?.id) {
          console.log('handleUpgrade - Updating local subscription state with new ID:', response.subscription.id);
          setSubscription(prev => ({
            ...prev,
            id: response.subscription.id,
            status: 'active',
            amount: totalAmount.toFixed(2)
          }));
        }
        
        // Add a small delay to ensure state is properly updated
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        await fetchCompanyDetails();
        
        // Debug subscription state after update
        console.log('handleUpgrade - State after fetchCompanyDetails:');
        debugSubscriptionState();
        
        setSelectedPackage(null);
        setPaymentDetails(null);
        showToast(
          (company?.package?.name === 'Enterprise' || company?.package?.name === 'Business Plus')
            ? 'Plan switched successfully! Your new plan is now active.'
            : 'Subscription upgraded successfully! Your new plan is now active.',
          'success'
        );
      } else {
        throw new Error(response.message || 'Failed to upgrade subscription');
      }
    } catch (err) {
      console.error('Error upgrading subscription:', err);
      console.error('Error details:', {
        code: err.code,
        message: err.message,
        details: err.details
      });
              setError(err.message || `Failed to ${(company?.package?.name === 'Enterprise' || company?.package?.name === 'Business Plus') ? 'switch plans' : 'upgrade subscription'}. Please try again.`);
    } finally {
      setUpgrading(false);
      setUpgradingStep(0);
      
      // Scroll to top when upgrade is complete
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBillingCycleChange = async () => {
    try {
      console.log('Starting billing cycle change process...');
      setChangingBillingCycle(true);
      setChangingBillingCycleStep(1);
      setError(null);

      // Close the dialog immediately when billing cycle change starts
      setCycleDialogOpen(false);
      
      // Scroll to top when loading starts
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Add a delay to show step 1
      await new Promise(resolve => setTimeout(resolve, 2000));

      if (!paymentDetails) {
        throw new Error('Please provide payment details');
      }

      // Get user data from auth
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }

      // Get user's name from company data or fallback to email
      const userName = `${company?.ownerFirstName || ''} ${company?.ownerLastName || ''}`.trim() || 
                      currentUser.email;

      const currentBillingCycle = company.package.subscription.billingCycle;
      const newBillingCycle = currentBillingCycle === 'monthly' ? 'annually' : 'monthly';

      const response = await changeBillingCycle({
        name: company.package.name, // Use package name instead of customer name
        currentAmount: company.package.subscription.currentPrice,
        currentBillingCycle: currentBillingCycle,
        newBillingCycle: newBillingCycle,
        vault: paymentDetails,
        metadata: {
          email: currentUser.email,
          businessId: company.id,
          packageId: company.package.id,
          packageName: company.package.name,
          subscriptionId: subscription.id,
          companyName: company.name || '',
          phone: company.phone || '',
          customerName: `${company?.ownerFirstName || ''} ${company?.ownerLastName || ''}`.trim() || currentUser.email,
          customerProfileId: company.package.subscription?.customerProfileId,
          customerPaymentProfileId: company.package.subscription?.customerPaymentProfileId,
          billingAddress: {
            firstName: company?.ownerFirstName || '',
            lastName: company?.ownerLastName || '',
            street: company.address?.street || '',
            city: company.address?.city || '',
            state: company.address?.state || '',
            zipCode: company.address?.zipCode || '',
            country: 'USA'
          }
        }
      });

      setChangingBillingCycleStep(2);
      // Add a delay before the API call to show step 2
      await new Promise(resolve => setTimeout(resolve, 1500));

      if (response.success) {
        setChangingBillingCycleStep(3);
        // Add a longer delay to show step 3
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        await fetchCompanyDetails();
        setPaymentDetails(null);
        showToast('Billing cycle changed successfully! Your new billing cycle is now active.', 'success');
      } else {
        throw new Error(response.message || 'Failed to change billing cycle');
      }
    } catch (err) {
      console.error('Error changing billing cycle:', err);
      setError(err.message || 'Failed to change billing cycle. Please try again.');
    } finally {
      setChangingBillingCycle(false);
      setChangingBillingCycleStep(0);
      
      // Scroll to top when billing cycle change is complete
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleTrialConversion = async () => {
    try {
      setProcessing(true);
      setError(null);

      if (!selectedPackage || !paymentDetails) {
        throw new Error('Please select a package and provide payment details');
      }

      // Validate billing address
      const { firstName, lastName, street, city, state, zipCode } = trialBillingAddress;
      if (!firstName || !lastName || !street || !city || !state || !zipCode) {
        setError('Please fill in all required billing address fields');
        setProcessing(false);
        return;
      }

      // Basic ZIP code validation
      if (!/^\d{5}(-\d{4})?$/.test(zipCode)) {
        setError('Please enter a valid ZIP code');
        setProcessing(false);
        return;
      }

      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }

      if (!currentUser.email) {
        throw new Error('User email is required');
      }

      console.log('handleTrialConversion - Current user:', {
        uid: currentUser.uid,
        email: currentUser.email
      });

      // Get user's name from billing address
      const userName = `${firstName} ${lastName}`.trim();

      // Calculate base amount based on billing cycle
      let baseAmount = parseFloat(selectedPackage.price.monthly);
      if (selectedBillingCycle === 'annually') {
        baseAmount = (baseAmount * 12) * 0.8; // 20% discount for annual
      }

      // Calculate tax (8.25%)
      const TAX_RATE = 0.0825;
      const taxAmount = baseAmount * TAX_RATE;
      const totalAmount = baseAmount + taxAmount;

      const conversionData = {
        name: selectedPackage.name, // Use package name instead of customer name
        email: currentUser.email,
        amount: totalAmount.toFixed(2),
        baseAmount: baseAmount.toFixed(2),
        taxAmount: taxAmount.toFixed(2),
        taxRate: TAX_RATE,
        billingCycle: selectedBillingCycle,
        vault: paymentDetails,
        metadata: {
          email: currentUser.email,
          businessId: company.id,
          packageId: selectedPackage.id,
          packageName: selectedPackage.name,
          companyName: company.name || '',
          phone: company.phone || '',
          isTrialConversion: true,
          customerName: userName,
          firstName,
          lastName,
          description: `${company.name || userName} - ${selectedPackage.name} Plan`,
          billingAddress: {
            firstName,
            lastName,
            street,
            city,
            state,
            zipCode,
            country: 'USA'
          }
        }
      };

      // Log the complete request data
      console.log('handleTrialConversion - Complete request data:', {
        ...conversionData,
        paymentDetails: '***REDACTED***',
        metadata: {
          ...conversionData.metadata,
          email: currentUser.email,
          customerName: userName,
          companyName: company.name || '',
          billingAddress: conversionData.metadata.billingAddress
        }
      });

      const response = await convertTrialToPaid(conversionData);

      console.log('handleTrialConversion - Response:', response);

      if (response.success) {
        await fetchCompanyDetails();
        setTrialConversionDialogOpen(false);
        setSelectedPackage(null);
        setPaymentDetails(null);
      } else {
        throw new Error(response.message || 'Failed to convert trial to paid subscription');
      }
    } catch (err) {
      console.error('Error converting trial:', err);
      console.error('Error details:', {
        code: err.code,
        message: err.message,
        details: err.details
      });
      setError(err.message || 'Failed to convert trial to paid subscription. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // Calculate days remaining in trial
  const getTrialDaysRemaining = () => {
    if (!subscription?.nextBillingDate) return 0;
    const now = new Date();
    const trialEnd = new Date(subscription.nextBillingDate);
    const diffTime = trialEnd - now;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Debug function to check subscription state
  const debugSubscriptionState = () => {
    console.log('🔍 Debug Subscription State:', {
      localSubscription: subscription,
      companyPackageSubscription: company?.package?.subscription,
      subscriptionId: subscription?.id,
      companySubscriptionId: company?.package?.subscription?.id,
      subscriptionStatus: subscription?.status,
      companySubscriptionStatus: company?.package?.subscription?.status
    });
  };

  // Update the Select component in both dialogs
  const renderPackageOptions = () => {
    return availablePackages
      .sort((a, b) => {
        const priceA = a.price?.monthly || 0;
        const priceB = b.price?.monthly || 0;
        return priceA - priceB;
      })
      .map((pkg) => (
        <MenuItem key={pkg.id} value={pkg.id}>
          {pkg.name} - ${pkg.price?.monthly || 0}/month
        </MenuItem>
      ));
  };

  if (loading) {
    return (
        <div className="accountSettingsPage manageSubscriptionPage" style={cssVars}>
          <div className="accountSettingsInner">
            <div className="accountSettingsHeader">
              <div>
                <h1>Billing</h1>
                <p>Manage your subscription and billing.</p>
              </div>
            </div>
            <section className="asCard" aria-label="Loading">
              <PageSkeleton showHeader={false} variant="form" rows={5} label="Loading billing" />
            </section>
          </div>
        </div>
    );
  }

  if (error) {
    return (
        <div className="accountSettingsPage manageSubscriptionPage" style={cssVars}>
          <div className="accountSettingsInner">
            <div className="accountSettingsHeader">
              <div>
                <h1>Billing</h1>
                <p>Manage your subscription and billing.</p>
              </div>
            </div>
            <section className="asCard" aria-label="Error">
              <div className="asCardBody">
                <p className="asErrorText">{error}</p>
              </div>
            </section>
          </div>
        </div>
    );
  }

  if (!company?.package) {
    return (
        <div className="accountSettingsPage manageSubscriptionPage" style={cssVars}>
          <div className="accountSettingsInner">
            <div className="accountSettingsHeader">
              <div>
                <h1>Billing</h1>
                <p>Manage your subscription and billing.</p>
              </div>
            </div>
            <section className="asCard" aria-label="No package">
              <div className="asCardBody">
                <p className="asHelperText">
                  No package is currently assigned to your account. Please contact support to set up your subscription.
                </p>
              </div>
            </section>
          </div>
        </div>
    );
  }

  const showAllForAdminOrPerm = company?.package?.isAdminPackage || subscription?.isPermanent;

  return (
    <ThemeProvider theme={muiTheme}>
      <>
        {renewing && (
          <LoadingOverlay>
            <LoadingContainer>
              <LoadingTitle>
                Renewing Your Subscription
              </LoadingTitle>
              
              <LoadingSubtitle>
                We're processing your renewal request. Your subscription will be reactivated immediately.
              </LoadingSubtitle>
              
              <ProgressContainer>
                <ProgressStep completed={renewingStep >= 1}>
                  <StepIcon completed={renewingStep >= 1}>
                    {renewingStep >= 1 ? <CheckIcon fontSize="small" /> : '1'}
                  </StepIcon>
                  <StepText completed={renewingStep >= 1}>
                    Validating renewal request
                  </StepText>
                </ProgressStep>
                
                <ProgressStep completed={renewingStep >= 2}>
                  <StepIcon completed={renewingStep >= 2}>
                    {renewingStep >= 2 ? <CheckIcon fontSize="small" /> : '2'}
                  </StepIcon>
                  <StepText completed={renewingStep >= 2}>
                    Processing renewal
                  </StepText>
                </ProgressStep>
                
                <ProgressStep completed={renewingStep >= 3}>
                  <StepIcon completed={renewingStep >= 3}>
                    {renewingStep >= 3 ? <CheckIcon fontSize="small" /> : '3'}
                  </StepIcon>
                  <StepText completed={renewingStep >= 3}>
                    Updating account status
                  </StepText>
                </ProgressStep>
              </ProgressContainer>
              
              {renewingStep < 3 && (
                <SpinnerContainer>
                  <CustomSpinner />
                </SpinnerContainer>
              )}
              
              <SecurityBadge>
                <SecurityIcon fontSize="small" />
                <span>Secure renewal processing</span>
              </SecurityBadge>
            </LoadingContainer>
          </LoadingOverlay>
        )}
        {upgrading && (
          <LoadingOverlay>
            <LoadingContainer>
              <LoadingTitle>
                {(company?.package?.name === 'Enterprise' || company?.package?.name === 'Business Plus') ? 'Switching Your Plan' : 'Upgrading Your Subscription'}
              </LoadingTitle>
              
              <LoadingSubtitle>
                {(company?.package?.name === 'Enterprise' || company?.package?.name === 'Business Plus')
                  ? 'We\'re processing your plan switch request. Your new plan will be activated immediately.'
                  : 'We\'re processing your upgrade request. Your new plan will be activated immediately.'
                }
              </LoadingSubtitle>
              
              <ProgressContainer>
                <ProgressStep completed={upgradingStep >= 1}>
                  <StepIcon completed={upgradingStep >= 1}>
                    {upgradingStep >= 1 ? <CheckIcon fontSize="small" /> : '1'}
                  </StepIcon>
                  <StepText completed={upgradingStep >= 1}>
                    {(company?.package?.name === 'Enterprise' || company?.package?.name === 'Business Plus') ? 'Validating plan switch request' : 'Validating upgrade request'}
                  </StepText>
                </ProgressStep>
                
                <ProgressStep completed={upgradingStep >= 2}>
                  <StepIcon completed={upgradingStep >= 2}>
                    {upgradingStep >= 2 ? <CheckIcon fontSize="small" /> : '2'}
                  </StepIcon>
                  <StepText completed={upgradingStep >= 2}>
                    {(company?.package?.name === 'Enterprise' || company?.package?.name === 'Business Plus') ? 'Processing plan switch' : 'Processing upgrade'}
                  </StepText>
                </ProgressStep>
                
                <ProgressStep completed={upgradingStep >= 3}>
                  <StepIcon completed={upgradingStep >= 3}>
                    {upgradingStep >= 3 ? <CheckIcon fontSize="small" /> : '3'}
                  </StepIcon>
                  <StepText completed={upgradingStep >= 3}>
                    Updating account status
                  </StepText>
                </ProgressStep>
              </ProgressContainer>
              
              {upgradingStep < 3 && (
                <SpinnerContainer>
                  <CustomSpinner />
                </SpinnerContainer>
              )}
              
              <SecurityBadge>
                <SecurityIcon fontSize="small" />
                <span>Secure {(company?.package?.name === 'Enterprise' || company?.package?.name === 'Business Plus') ? 'plan switch' : 'upgrade'} processing</span>
              </SecurityBadge>
            </LoadingContainer>
          </LoadingOverlay>
        )}
        {changingBillingCycle && (
          <LoadingOverlay>
            <LoadingContainer>
              <LoadingTitle>
                Changing Your Billing Cycle
              </LoadingTitle>
              
              <LoadingSubtitle>
                We're processing your billing cycle change request. Your new billing cycle will be activated immediately.
              </LoadingSubtitle>
              
              <ProgressContainer>
                <ProgressStep completed={changingBillingCycleStep >= 1}>
                  <StepIcon completed={changingBillingCycleStep >= 1}>
                    {changingBillingCycleStep >= 1 ? <CheckIcon fontSize="small" /> : '1'}
                  </StepIcon>
                  <StepText completed={changingBillingCycleStep >= 1}>
                    Validating billing cycle change
                  </StepText>
                </ProgressStep>
                
                <ProgressStep completed={changingBillingCycleStep >= 2}>
                  <StepIcon completed={changingBillingCycleStep >= 2}>
                    {changingBillingCycleStep >= 2 ? <CheckIcon fontSize="small" /> : '2'}
                  </StepIcon>
                  <StepText completed={changingBillingCycleStep >= 2}>
                    Processing billing cycle change
                  </StepText>
                </ProgressStep>
                
                <ProgressStep completed={changingBillingCycleStep >= 3}>
                  <StepIcon completed={changingBillingCycleStep >= 3}>
                    {changingBillingCycleStep >= 3 ? <CheckIcon fontSize="small" /> : '3'}
                  </StepIcon>
                  <StepText completed={changingBillingCycleStep >= 3}>
                    Updating account status
                  </StepText>
                </ProgressStep>
              </ProgressContainer>
              
              {changingBillingCycleStep < 3 && (
                <SpinnerContainer>
                  <CustomSpinner />
                </SpinnerContainer>
              )}
              
              <SecurityBadge>
                <SecurityIcon fontSize="small" />
                <span>Secure billing cycle processing</span>
              </SecurityBadge>
            </LoadingContainer>
          </LoadingOverlay>
        )}
        <div className="accountSettingsPage manageSubscriptionPage" style={cssVars}>
          <div className="accountSettingsInner">
            <div className="accountSettingsHeader">
              <div>
                <h1>Billing</h1>
                <p>Manage your subscription and billing.</p>
              </div>
              {!embedded ? (
                <PrimaryButton type="button" onClick={() => navigate('/app/overview')}>
                  Back to app
                </PrimaryButton>
              ) : null}
            </div>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {subscription?.status === 'cancelled' && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Your subscription has been cancelled and will end on {formatDate(subscription.nextBillingDate)}.
                You can renew your subscription anytime before it expires to maintain access to all features.
              </Alert>
            )}

            {subscription?.status === 'trial' && (
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                  <PrimaryButton type="button" onClick={() => setTrialConversionDialogOpen(true)}>
                    Upgrade Now
                  </PrimaryButton>
                }
              >
                Your free trial will end in {getTrialDaysRemaining()} days. Choose a plan to continue using our services.
              </Alert>
            )}

            <section className="asCard" aria-label="Current subscription">
              <div className="asCardHeader">
                <h2>Current Subscription</h2>
                <p className="sub">Your plan details and billing options.</p>
              </div>
              <div className="asCardBody manageSubscriptionGrid">
                <div className="manageSubscriptionBox">
                  <div className="manageSubscriptionBoxTitle">Plan & Billing</div>
                  <div className="manageSubscriptionRow">
                    <label className="asLabel">Status</label>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {getSubscriptionStatusBadge()}
                    </span>
                  </div>
                  <div className="manageSubscriptionRow">
                    <label className="asLabel">Plan</label>
                    <p className="manageSubscriptionPlanName">
                      {currentPackage?.name || company?.package?.name}
                    </p>
                  </div>
                  <div className="asDivider" />
                  <div className="manageSubscriptionRow">
                    <label className="asLabel">{subscription?.status === 'cancelled' ? 'Expires on' : 'Next payment due'}</label>
                    <span className={`manageSubscriptionValue ${!(subscription?.nextBillingDate || subscription?.status === 'trial') ? 'manageSubscriptionValueMuted' : ''}`}>
                      {subscription?.status === 'trial'
                        ? (subscription?.nextBillingDate ? `Trial ends ${formatDate(subscription.nextBillingDate)}` : '—')
                        : (subscription?.nextBillingDate ? formatDate(subscription.nextBillingDate) : 'N/A')}
                    </span>
                  </div>
                  <div className="manageSubscriptionRow">
                    <label className="asLabel">Price</label>
                    <span className="manageSubscriptionValue">
                      ${subscription?.currentPrice || '0.00'}/{subscription?.billingCycle === 'annually' ? 'year' : 'month'}
                    </span>
                  </div>
                  {subscription?.status !== 'trial' && (
                    <>
                      <div className="asDivider" />
                      <div className="manageSubscriptionRow">
                        <label className="asLabel">Auto-renew</label>
                        <span className="manageSubscriptionValue">{subscription?.autoRenew ? 'Yes' : 'No'}</span>
                      </div>
                    </>
                  )}
                  {subscription?.trialDays > 0 && subscription?.status === 'trial' && (
                    <>
                      <div className="asDivider" />
                      <div className="manageSubscriptionRow">
                        <label className="asLabel">Trial period</label>
                        <span className="manageSubscriptionValue">{subscription.trialDays} days</span>
                      </div>
                    </>
                  )}
                  {((!subscription?.isPermanent && subscription?.status === 'active') || showAllForAdminOrPerm) && (
                    <p className="asHelperText manageSubscriptionBillingLinkWrap" style={{ margin: '10px 0 0 0' }}>
                      <button
                        type="button"
                        className="manageSubscriptionLink"
                        onClick={() => setCycleDialogOpen(true)}
                        disabled={upgrading || changingBillingCycle}
                      >
                        {changingBillingCycle ? 'Processing...' : `Switch to ${company?.package?.subscription?.billingCycle === 'annually' ? 'Monthly' : 'Annual'} Billing →`}
                      </button>
                    </p>
                  )}
                  {((!subscription?.isPermanent && subscription?.status === 'active') || showAllForAdminOrPerm) && availablePackages.length > 0 && (
                    <p className="asHelperText manageSubscriptionBillingLinkWrap" style={{ margin: '6px 0 0 0' }}>
                      {(company?.package?.name === 'Enterprise' || company?.package?.name === 'Business Plus') && (
                        <button
                          type="button"
                          className="manageSubscriptionLink"
                          onClick={() => setUpgradeDialogOpen(true)}
                          disabled={upgrading || changingBillingCycle}
                        >
                          {upgrading ? 'Processing...' : 'Switch plans →'}
                        </button>
                      )}
                      {company?.package?.name === 'Business' && (
                        <button
                          type="button"
                          className="manageSubscriptionLink"
                          onClick={() => setUpgradeDialogOpen(true)}
                          disabled={upgrading || changingBillingCycle}
                        >
                          {upgrading ? 'Processing...' : 'Upgrade plan →'}
                        </button>
                      )}
                    </p>
                  )}
                </div>

                <div className="manageSubscriptionBox">
                  <div className="manageSubscriptionBoxTitle">Payment method</div>
                  <label className="asLabel" style={{ display: 'block', marginBottom: 4 }}>Card on file</label>
                  <div className="manageSubscriptionPaymentBlock">
                    <div className="manageSubscriptionCardIcon" aria-hidden="true" />
                    <span className="manageSubscriptionCardFiller">
                      •••• •••• •••• {company?.package?.subscription?.paymentMethodLast4 || subscription?.paymentMethodLast4 || '----'}
                    </span>
                  </div>
                  <p className="manageSubscriptionCardFinePrint">
                    We only store the last 4 digits; the full card number is never stored. Payment processing is handled securely by Deluxe.
                  </p>
                  {(subscription?.status === 'cancelled' || showAllForAdminOrPerm) && (
                    <p className="asHelperText manageSubscriptionBillingLinkWrap" style={{ margin: '10px 0 0 0' }}>
                      <button
                        type="button"
                        className="manageSubscriptionLink"
                        onClick={() => setRenewDialogOpen(true)}
                        disabled={renewing}
                      >
                        {renewing ? 'Processing...' : 'Renew subscription →'}
                      </button>
                    </p>
                  )}
                  {(subscription?.status === 'active' || showAllForAdminOrPerm) && (
                    <p className="asHelperText manageSubscriptionBillingLinkWrap" style={{ margin: '10px 0 0 0' }}>
                      <button
                        type="button"
                        className="manageSubscriptionLink manageSubscriptionLinkDanger"
                        onClick={() => setCancelDialogOpen(true)}
                        disabled={cancelling}
                      >
                        Cancel subscription
                      </button>
                    </p>
                  )}
                </div>
              </div>
            </section>

            <section className="asCard" aria-label="Billing history" style={{ marginTop: 18 }}>
              <div className="asCardHeader">
                <h2>Billing History</h2>
                <p className="sub">Past invoices and payments.</p>
              </div>
              <div className="asCardBody">
                {billingHistory.length > 0 ? (
                  <TableContainer component={Paper} className="billingHistoryTable">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {billingHistory.map((record) => (
                          <TableRow key={record.id}>
                            <TableCell>{formatBillingDate(record.timestamp)}</TableCell>
                            <TableCell>
                              {record.description || `${record.billingCycle || ''} subscription payment`.trim() || 'Payment'}
                            </TableCell>
                            <TableCell>{formatBillingAmount(record.amount)}</TableCell>
                            <TableCell>
                              <span
                                className={`asVerifyBadge ${
                                  record.status === 'completed' ? 'verified' :
                                  record.status === 'failed' ? 'unverified' : 'comingSoon'
                                }`}
                              >
                                {record.status}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <p className="asHelperText" style={{ margin: 0 }}>No billing history available.</p>
                )}
              </div>
            </section>
          </div>
        </div>

        {/* Cancel Subscription Dialog */}
        <Dialog
              open={cancelDialogOpen}
              onClose={() => !cancelling && setCancelDialogOpen(false)}
            >
              <DialogTitle>Cancel Subscription</DialogTitle>
              <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                  Are you sure you want to cancel your subscription? Your subscription will remain active until {formatDate(subscription?.nextBillingDate)}, after which you will lose access to premium features.
                </DialogContentText>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Reason for cancellation (optional)"
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  disabled={cancelling}
                />
              </DialogContent>
              <DialogActions>
                <PrimaryButton type="button" onClick={() => setCancelDialogOpen(false)} disabled={cancelling}>
                  Keep Subscription
                </PrimaryButton>
                <PrimaryButton type="button" className="manageSubscriptionDialogCancelConfirm" onClick={handleCancelSubscription} disabled={cancelling}>
                  {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
                </PrimaryButton>
              </DialogActions>
            </Dialog>

            {/* Renew Subscription Dialog */}
            <Dialog
              open={renewDialogOpen}
              onClose={() => !renewing && setRenewDialogOpen(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>Renew Subscription</DialogTitle>
              <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                  Please provide your payment details to renew your subscription. You will be charged ${subscription?.currentPrice} {subscription?.billingCycle === 'annually' ? 'per year' : 'per month'}.
                </DialogContentText>
                
                <DeluxeVaultSetup
                  onVaultReady={setPaymentDetails}
                  disabled={renewing}
                />
              </DialogContent>
              <DialogActions>
                <PrimaryButton type="button" onClick={() => setRenewDialogOpen(false)} disabled={renewing}>
                  Cancel
                </PrimaryButton>
                <PrimaryButton
                  type="button"
                  onClick={handleRenewSubscription}
                  disabled={renewing || !paymentDetails}
                >
                  {renewing ? 'Processing...' : 'Renew Subscription'}
                </PrimaryButton>
              </DialogActions>
            </Dialog>

            {/* Upgrade Dialog */}
            <Dialog
              open={upgradeDialogOpen}
              onClose={() => !upgrading && setUpgradeDialogOpen(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>{(company?.package?.name === 'Enterprise' || company?.package?.name === 'Business Plus') ? 'Switch Plans' : 'Upgrade Your Plan'}</DialogTitle>
              <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                  {(company?.package?.name === 'Enterprise' || company?.package?.name === 'Business Plus')
                    ? 'Choose a different plan. You can switch to any available plan. Your billing cycle will remain the same.'
                    : 'Choose a higher tier plan to unlock more features. Your billing cycle will remain the same.'
                  }
                </DialogContentText>

                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Select New Plan</InputLabel>
                  <Select
                    value={selectedPackage?.id || ''}
                    onChange={(e) => {
                      const pkg = availablePackages.find(p => p.id === e.target.value);
                      setSelectedPackage(pkg);
                    }}
                    label="Select New Plan"
                  >
                    {renderPackageOptions()}
                  </Select>
                </FormControl>

                <DeluxeVaultSetup
                  onVaultReady={setPaymentDetails}
                  disabled={processing}
                />
              </DialogContent>
              <DialogActions>
                <PrimaryButton type="button" onClick={() => setUpgradeDialogOpen(false)} disabled={upgrading}>
                  Cancel
                </PrimaryButton>
                <PrimaryButton
                  type="button"
                  onClick={handleUpgrade}
                  disabled={upgrading || !selectedPackage || !paymentDetails}
                >
                  {upgrading ? 'Processing...' : (company?.package?.name === 'Enterprise' || company?.package?.name === 'Business Plus') ? 'Switch Plans' : 'Upgrade Now'}
                </PrimaryButton>
              </DialogActions>
            </Dialog>

            {/* Billing Cycle Change Dialog */}
            <Dialog
              open={cycleDialogOpen}
              onClose={() => !changingBillingCycle && setCycleDialogOpen(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>Change Billing Cycle</DialogTitle>
              <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                  {company?.package?.subscription?.billingCycle === 'annually' ? (
                    'Switch to monthly billing. Your new rate will be calculated on a monthly basis.'
                  ) : (
                    'Switch to annual billing and save 20% compared to monthly billing.'
                  )}
                </DialogContentText>

                <DeluxeVaultSetup
                  onVaultReady={setPaymentDetails}
                  disabled={changingBillingCycle}
                />
              </DialogContent>
              <DialogActions>
                <PrimaryButton type="button" onClick={() => setCycleDialogOpen(false)} disabled={changingBillingCycle}>
                  Cancel
                </PrimaryButton>
                <PrimaryButton
                  type="button"
                  onClick={handleBillingCycleChange}
                  disabled={changingBillingCycle || !paymentDetails}
                >
                  {changingBillingCycle ? 'Processing...' : 'Change Billing Cycle'}
                </PrimaryButton>
              </DialogActions>
            </Dialog>

            {/* Trial Conversion Dialog */}
            <Dialog
              open={trialConversionDialogOpen}
              onClose={() => !processing && setTrialConversionDialogOpen(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>Choose Your Plan</DialogTitle>
              <DialogContent>
                <LoadingBackdrop open={processing}>
                  <CircularProgress color="primary" size={60} />
                  <Typography variant="h6" color="white">
                    Setting up your subscription...
                  </Typography>
                  <Typography variant="body2" color="white" align="center">
                    Please wait while we process your payment and set up your account.
                    This may take a few moments.
                  </Typography>
                </LoadingBackdrop>

                <DialogContentText sx={{ mb: 2 }}>
                  Select a plan to continue using our services after your trial ends. Your new plan will start immediately.
                </DialogContentText>

                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Select Plan</InputLabel>
                  <Select
                    value={selectedPackage?.id || ''}
                    onChange={(e) => {
                      const pkg = availablePackages.find(p => p.id === e.target.value);
                      setSelectedPackage(pkg);
                    }}
                    label="Select Plan"
                  >
                    {renderPackageOptions()}
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Billing Cycle</InputLabel>
                  <Select
                    value={selectedBillingCycle}
                    onChange={(e) => setSelectedBillingCycle(e.target.value)}
                    label="Billing Cycle"
                  >
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="annually">Annually (20% discount)</MenuItem>
                  </Select>
                </FormControl>

                {selectedPackage && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Price Breakdown:
                    </Typography>
                    <Box sx={{ pl: 2 }}>
                      {(() => {
                        // Ensure we're working with numbers
                        const monthlyPrice = parseFloat(selectedPackage.price?.monthly) || 0;
                        const basePrice = selectedBillingCycle === 'annually'
                          ? (monthlyPrice * 12 * 0.8)
                          : monthlyPrice;
                        const taxAmount = basePrice * 0.0825;
                        const total = basePrice + taxAmount;

                        return (
                          <>
                            <Typography variant="body2" color="text.secondary">
                              Base Price: ${basePrice.toFixed(2)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Tax (8.25%): ${taxAmount.toFixed(2)}
                            </Typography>
                            <Typography variant="subtitle2" sx={{ mt: 1 }}>
                              Total: ${total.toFixed(2)}/{selectedBillingCycle === 'annually' ? 'year' : 'month'}
                            </Typography>
                          </>
                        );
                      })()}
                    </Box>
                  </Box>
                )}

                <Typography variant="h6" sx={{ mb: 2 }}>
                  Billing Address
                </Typography>

                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label="First Name"
                      value={trialBillingAddress.firstName}
                      onChange={(e) => setTrialBillingAddress(prev => ({
                        ...prev,
                        firstName: e.target.value
                      }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label="Last Name"
                      value={trialBillingAddress.lastName}
                      onChange={(e) => setTrialBillingAddress(prev => ({
                        ...prev,
                        lastName: e.target.value
                      }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="Street Address"
                      value={trialBillingAddress.street}
                      onChange={(e) => setTrialBillingAddress(prev => ({
                        ...prev,
                        street: e.target.value
                      }))}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <HomeIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label="City"
                      value={trialBillingAddress.city}
                      onChange={(e) => setTrialBillingAddress(prev => ({
                        ...prev,
                        city: e.target.value
                      }))}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationCityIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Autocomplete
                      options={US_STATES}
                      getOptionLabel={(option) => option.label}
                      value={US_STATES.find(state => state.value === trialBillingAddress.state) || null}
                      onChange={(event, newValue) => {
                        setTrialBillingAddress(prev => ({
                          ...prev,
                          state: newValue ? newValue.value : ''
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          label="State"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      required
                      label="ZIP Code"
                      value={trialBillingAddress.zipCode}
                      onChange={(e) => setTrialBillingAddress(prev => ({
                        ...prev,
                        zipCode: e.target.value
                      }))}
                      placeholder="12345"
                      inputProps={{ maxLength: 10 }}
                    />
                  </Grid>
                </Grid>

                <DeluxeVaultSetup
                  onVaultReady={setPaymentDetails}
                  disabled={processing}
                />
              </DialogContent>
              <DialogActions>
                <PrimaryButton type="button" onClick={() => setTrialConversionDialogOpen(false)} disabled={processing}>
                  Cancel
                </PrimaryButton>
                <PrimaryButton
                  type="button"
                  onClick={handleTrialConversion}
                  disabled={processing || !selectedPackage || !paymentDetails}
                >
                  {processing ? 'Processing...' : 'Start Subscription'}
                </PrimaryButton>
              </DialogActions>
            </Dialog>
      </>
      
    </ThemeProvider>
  );
};

export default ManageSubscription; 